// src/components/nationwide/CheckinCard.jsx
import React, { useState } from "react";
import { Eye, X } from "lucide-react";

const CheckinCard = ({ checkin }) => {
  const [previewFile, setPreviewFile] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "SUBMITTED":
        return "bg-blue-100 text-blue-800";
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "ON_HOLD":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    return status
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  const isPreviewable = (file) => {
    const contentType = file.contentType || "";
    return (
      contentType.includes("image") ||
      contentType.includes("pdf") ||
      contentType.includes("jpg") ||
      contentType.includes("jpeg") ||
      contentType.includes("png")
    );
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
  };

  const closePreview = () => {
    setIsClosing(true);
  };

  const handleAnimationEnd = () => {
    if (isClosing) {
      setPreviewFile(null);
      setIsClosing(false);
    }
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-gray-900">
                Verification Submission
              </h4>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                  checkin.processingStatus
                )}`}
              >
                {getStatusLabel(checkin.processingStatus)}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Submitted {formatDate(checkin.checkedInAt)}
            </p>
          </div>
        </div>

        {/* Files */}
        {checkin.files && checkin.files.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">
              Uploaded Documents ({checkin.files.length})
            </p>
            {checkin.files.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm"
              >
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="flex-1 truncate text-gray-700">
                  {file.originalFileName}
                </span>
                <span className="text-xs text-gray-500">
                  {(file.fileSizeBytes / 1024).toFixed(1)} KB
                </span>
                {isPreviewable(file) && (
                  <button
                    onClick={() => handlePreview(file)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    title="Preview document"
                  >
                    <Eye className="w-4 h-4 text-blue-600" />
                  </button>
                )}
                {file.errorFlag && (
                  <svg
                    className="w-4 h-4 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Survey Inputs */}
        {checkin.inputs && checkin.inputs.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
              Provided Information
            </p>
            <div className="space-y-1">
              {checkin.inputs.slice(0, 3).map((input) => (
                <div key={input.id} className="flex gap-2 text-xs">
                  <span className="text-gray-600">{input.fieldName}:</span>
                  <span className="text-gray-900 font-medium">
                    {input.fieldValue}
                  </span>
                </div>
              ))}
              {checkin.inputs.length > 3 && (
                <p className="text-xs text-gray-500 italic">
                  +{checkin.inputs.length - 3} more field
                  {checkin.inputs.length - 3 !== 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {checkin.userNote && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-100 rounded text-sm">
            <p className="text-xs font-medium text-blue-900 mb-1">Your Note:</p>
            <p className="text-blue-800">{checkin.userNote}</p>
          </div>
        )}

        {checkin.adminNote && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-100 rounded text-sm">
            <p className="text-xs font-medium text-amber-900 mb-1">
              Admin Note:
            </p>
            <p className="text-amber-800">{checkin.adminNote}</p>
          </div>
        )}

        {checkin.partnerNote && (
          <div className="mt-3 p-2 bg-purple-50 border border-purple-100 rounded text-sm">
            <p className="text-xs font-medium text-purple-900 mb-1">
              Partner Note:
            </p>
            <p className="text-purple-800">{checkin.partnerNote}</p>
          </div>
        )}

        {checkin.onHoldNote && checkin.processingStatus === "ON_HOLD" && (
          <div className="mt-3 p-2 bg-orange-50 border border-orange-100 rounded text-sm">
            <p className="text-xs font-medium text-orange-900 mb-1">
              On Hold Reason:
            </p>
            <p className="text-orange-800">{checkin.onHoldNote}</p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 ${isClosing ? "dialog-overlay-exit" : "dialog-overlay-enter"}`}
          onAnimationEnd={handleAnimationEnd}
          onClick={closePreview}
        >
          <div
            className={`bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ${isClosing ? "dialog-content-exit" : "dialog-content-enter"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="font-semibold text-gray-900 truncate flex-1">
                {previewFile.originalFileName}
              </h3>
              <button
                onClick={closePreview}
                className="ml-4 p-2 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                aria-label="Close preview"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto bg-gray-100 p-4 flex items-center justify-center">
              {previewFile.contentType?.includes("pdf") ? (
                <iframe
                  src={previewFile.documentUrl}
                  className="w-full h-full min-h-[600px] bg-white rounded"
                  title={previewFile.originalFileName}
                />
              ) : (
                <img
                  src={previewFile.documentUrl}
                  alt={previewFile.originalFileName}
                  className="max-w-full max-h-full object-contain rounded"
                />
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Size:</span>{" "}
                {(previewFile.fileSizeBytes / 1024).toFixed(1)} KB
              </div>
              <a
                href={previewFile.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium underline"
              >
                Open in new tab
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckinCard;